// apps/client/src/pages/admin/dashboard/admin/page.tsx

import { useState } from "react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CheckCircleIcon,
  ExportIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserCheckIcon,
  UsersIcon,
  XCircleIcon,
  UserPlusIcon,
  DotsThreeIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  UserMinusIcon,
  PencilIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useFilterAdmin } from "@/client/services/user/administrators";
import { userFilterAdminStore } from "@/client/stores/filter-admin";
import { useDialog } from "@/client/stores/dialog";
import { useExportAdmin } from "@/client/services/user/export-admin-xlsx";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";
import { FilterUserType } from "@reactive-resume/dto";
import { SelectRangeDate } from "../_components/date-range-overview";
import { subDays } from "date-fns";
import { useUserStats } from "@/client/services/user/user-stats";
import { cn } from "@reactive-resume/utils";
import { useChangeActivation } from "@/client/services/user/activate-user";
import { useRoles } from "@/client/services/role/role";
import { toast } from "sonner";
import { useAdminDeleteUser } from "@/client/services/user/delete-user";

export const AdminAdminPage = () => {
  const { filterAdmin, replaceFilterAdmin } = userFilterAdminStore();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");
  const [verified, setVerified] = useState<"all" | "yes" | "no">("all");
  const [roleId, setRoleId] = useState<string>("all");

  const { filterAdmin: filterAdmins } = useFilterAdmin(page, filterAdmin);
  const { refetch } = useExportAdmin();
  const { ChStatus } = useChangeActivation();
  const { data: roles } = useRoles();

  // Dates pour les statistiques
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  // const { data: stats, isLoading: statsLoading } = useUserStats(
  //   dateRange.from,
  //   dateRange.to,
  //   true
  // );

  const dialogCreate = useDialog("create-user");
  const advancedFilters = useDialog("filter-admin");
  const profileDialog = useDialog<{ id: string }>("get-user");
  const rolesDialog = useDialog<{ userId: string; currentRoles: any[] }>("admin-roles");

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecificpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter: FilterUserType = { ...filterAdmin };
    delete nextFilter.name;
    delete nextFilter.email;
    delete nextFilter.isActivated;
    delete nextFilter.emailVerified;
    delete (nextFilter as any).roleId;

    if (search.trim()) {
      const searchField = search.includes("@") ? "email" : "name";
      nextFilter[searchField] = { contains: search.trim() };
    }
    if (status !== "all") nextFilter.isActivated = { equals: status === "active" };
    if (verified !== "all") nextFilter.emailVerified = { equals: verified === "yes" };
    if (roleId !== "all") (nextFilter as any).roleId = { equals: roleId };

    setPage(1);
    replaceFilterAdmin(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setVerified("all");
    setRoleId("all");
    setPage(1);
    replaceFilterAdmin(null);
  };

  const openProfile = (userId: string) => {
    profileDialog.open("read", { id: "get-user", item: { id: userId } });
  };

  const openRoles = (userId: string, currentRoles: any[]) => {
    rolesDialog.open("update", { id: "admin-roles", item: { userId, currentRoles } });
  };

  const { mutate: deleteUser, isPending: deleting } = useAdminDeleteUser();

  const handleDeleteUser = (user: any) => {
    if (confirm(`Voulez-vous vraiment supprimer "${user.name}" ? Cette action est irréversible.`)) {
      deleteUser(user.id, {
        onSuccess: () => toast.success("Utilisateur supprimé avec succès"),
        onError: () => toast.error("Erreur lors de la suppression"),
      });
    }
  };

  const handleEditUser = (item: any) => {
    dialogCreate.open("update", {
      id: "create-user",
      item: {
        userId: item.id,
        isInternal: item.isInternal,
        user: {
          name: item.name,
          email: item.email,
          isInternal: item.isInternal,
        },
        currentRoles: item.roles || [],
      },
    });
  };

  const columns: Column<any>[] = [
    {
      key: "name",
      header: "Utilisateur",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-admin-surface-elevated">
            <img
              src={item.picture || "/default-avatar.png"}
              alt={item.name}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-admin-text-primary">{item.name || "—"}</span>
            <span className="text-xs text-admin-text-muted">{item.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: "roles",
      header: "Rôles",
      render: (item) => {
        const roles = item.roles?.map((r: any) => r.name) || [];
        return (
          <div className="flex flex-wrap gap-1">
            {roles.length > 0 ? (
              roles.map((role: string) => (
                <Badge key={role} className="border-focus bg-focus text-xs text-white">
                  {role}
                </Badge>
              ))
            ) : (
              <span className="text-xs text-admin-text-muted">Aucun rôle</span>
            )}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Inscription",
      render: (item) =>
        new Date(item.createdAt).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      key: "updatedAt",
      header: "Dernière activité",
      render: (item) =>
        new Date(item.updatedAt).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    // {
    //   key: "emailVerified",
    //   header: "Vérifié",
    //   align: "center",
    //   render: (item) =>
    //     item.emailVerified ? (
    //       <CheckCircleIcon size={20} weight="fill" className="text-admin-success" />
    //     ) : (
    //       <XCircleIcon size={20} weight="fill" className="text-admin-text-disabled" />
    //     ),
    // },
    {
      key: "isActivated",
      header: "Statut",
      render: (item) => (
        <Badge variant={item.isActivated ? "success" : "error"}>
          {item.isActivated ? "Actif" : "Banni"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <div className="flex justify-end gap-1 text-admin-text-secondary">
          {/* Toggle activation (hors dropdown) */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Activer/Désactiver"
            onClick={() => ChStatus(item.id)}
            className={cn(
              "hover:text-admin-text-primary",
              item.isActivated ? "text-admin-success" : "text-admin-danger",
            )}
          >
            {item.isActivated ? <UserCheckIcon size={18} /> : <UserMinusIcon size={18} />}
          </Button>

          {/* Dropdown menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Plus d'actions"
                className="hover:text-admin-text-primary"
              >
                <DotsThreeIcon size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => openProfile(item.id)} className="cursor-pointer">
                <EyeIcon size={16} className="mr-2" /> Voir le profil
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openRoles(item.id, item.roles || [])}
                className="cursor-pointer"
              >
                <ShieldCheckIcon size={16} className="mr-2" /> Gérer les rôles
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleEditUser(item)} className="cursor-pointer">
                <PencilIcon size={16} className="mr-2" /> Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteUser(item)}
                disabled={deleting}
                className="cursor-pointer text-admin-danger focus:text-admin-danger"
              >
                <TrashIcon size={16} className="mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Administrateurs</h1>
          <p className="mt-1 text-sm text-admin-text-muted">Tableau de bord / Administrateurs</p>
        </div>
        {/* <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder="Rechercher..."
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="w-8 h-8 rounded-full bg-admin-primary overflow-hidden">
            <img src="/avatar.jpg" alt="User" className="w-full h-full object-cover" />
          </div>
        </div> */}
      </div>

      {/* Sélecteur de dates pour les statistiques */}
      {/* <div className="flex justify-end mb-6">
        <SelectRangeDate value={dateRange} onChange={setDateRange} />
      </div> */}

      {/* Bloc Filtres et Actions — Responsive */}
      <div className="mb-6 rounded-xl-admin border border-admin-border bg-admin-surface p-4 sm:mb-8 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:gap-5">
          {/* Ligne 1 : Recherche */}
          <div className="relative w-full">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder="Rechercher par nom ou email..."
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Ligne 2 : Selects */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:flex lg:flex-wrap">
            {/* Select Rôle */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Rôle</span>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger className="h-10 w-full bg-admin-background">
                  <SelectValue placeholder="Tous les rôles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  {roles?.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Select Statut */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Statut</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 w-full bg-admin-background">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Banni</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Vérifié</span>
              <Select value={verified} onValueChange={(value) => setVerified(value as typeof verified)}>
                <SelectTrigger className="h-10 min-w-24 bg-admin-background">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="yes">Oui</SelectItem>
                  <SelectItem value="no">Non</SelectItem>
                </SelectContent>
              </Select>
            </div> */}
            {/* </div>

          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 sm:gap-3"> */}
            <Button
              variant="outline"
              onClick={applyQuickFilters}
              className="mt-5 h-10 w-full border-admin-border bg-admin-background text-admin-text-secondary hover:text-admin-text-primary lg:w-auto"
            >
              <FunnelIcon size={16} className="mr-2" /> Filtrer
            </Button>
            <Button
              variant="outline"
              onClick={() => advancedFilters.open("create", { id: "filter-admin" })}
              className="mt-5 h-10 w-full border-admin-border bg-admin-background text-admin-text-secondary hover:text-admin-text-primary lg:w-auto"
            >
              <FunnelIcon size={16} className="mr-2" /> Filtres avancés
            </Button>
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="mt-5 h-10 w-full text-admin-text-muted hover:text-admin-text-primary lg:w-auto"
            >
              Réinitialiser
            </Button>
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="mt-5 h-10 w-full bg-admin-background text-admin-text-secondary hover:text-admin-text-primary lg:w-auto"
            >
              <ExportIcon size={16} className="mr-2" /> Exporter
            </Button>
            <Button
              onClick={() =>
                dialogCreate.open("create", {
                  id: "create-user",
                  item: { isInternal: true },
                })
              }
              className="col-span-2 mt-5 h-10 w-full bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90 sm:col-span-1 lg:w-auto"
            >
              <UserPlusIcon size={16} className="mr-2" /> Nouvel admin
            </Button>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <DataTable
        title="Liste des administrateurs"
        data={filterAdmins?.data ?? []}
        columns={columns}
        pageSize={8}
        className="shadow-sm-admin"
      />

      {/* Pagination */}
      <div className="my-3 flex flex-wrap items-center justify-end gap-2 sm:gap-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={28} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}
        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {filterAdmins && page + 1 <= filterAdmins.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {filterAdmins && page + 2 <= filterAdmins.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {filterAdmins && page < filterAdmins.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={28} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
